import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { fetchContinueCertification } from 'src/services/CertificationContinue.service';

interface TransferGuardResult {
  isChecking: boolean;
  shouldRender: boolean;
}

/**
 * Route guard hook: asks the certification "continue" resolver where this
 * renter should be, and redirects to the transfer page only when the resolver
 * still requires it.
 *
 * The resolver is the single source of truth for this question — the renter
 * home card, the certification hub and the data vault history preview all read
 * the same answer. Reading the transfer state directly here let the guard
 * disagree with those surfaces (it treated any pending transfer as blocking,
 * including one the renter had walked away from days ago).
 *
 * @param surveyId - The survey ID to check.
 * @param redirectTo - Path to navigate to when the resolver still asks for the
 *   transfer. Defaults to '../data-transfer' (relative), which works from the
 *   questionnaire route. Pass an absolute path (e.g.
 *   `/renter/interview/${surveyId}/data-transfer`) when calling from a
 *   route at a different nesting level.
 */
export function useTransferGuard(
  surveyId: number | undefined,
  redirectTo = '../data-transfer'
): TransferGuardResult {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (!surveyId || !Number.isFinite(surveyId)) {
      setIsChecking(false);
      setShouldRender(true);
      return;
    }

    let cancelled = false;

    fetchContinueCertification(surveyId)
      .then(({ redirect_to: redirectToTarget }) => {
        if (cancelled) {
          return;
        }
        if (redirectToTarget === 'data-transfer') {
          navigate(redirectTo, { replace: true });
        } else {
          setShouldRender(true);
        }
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        // On error, allow questionnaire (fail-open for renter experience)
        setShouldRender(true);
      })
      .finally(() => {
        if (!cancelled) {
          setIsChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [surveyId, navigate, redirectTo]);

  return { isChecking, shouldRender };
}
